import { useMemo, useState, useEffect } from "react";
import { TrendingUp, TrendingDown, ArrowLeftRight, Minus } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const ID_MONTHS = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

const monthLabel = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-").map(Number);
  return `${ID_MONTHS[m - 1]} ${y}`;
};

const fmtNum = (v, dec = 0) =>
  v == null ? "—" : Number(v).toLocaleString("id-ID", { maximumFractionDigits: dec });

function fmtEnergy(kwh) {
  if (kwh == null) return { v: "—", u: "" };
  const abs = Math.abs(kwh);
  if (abs >= 1e6) return { v: (kwh / 1e6).toLocaleString("id-ID", { maximumFractionDigits: 2 }), u: "GWh" };
  if (abs >= 1e3) return { v: (kwh / 1e3).toLocaleString("id-ID", { maximumFractionDigits: 1 }), u: "MWh" };
  return { v: kwh.toLocaleString("id-ID", { maximumFractionDigits: 0 }), u: "kWh" };
}

const COLOR_A = "#94a3b8";
const COLOR_B = "#1e40af";

function DeltaPct({ pct, className = "" }) {
  if (pct == null) return <span className={`text-slate-400 ${className}`}><Minus className="w-3 h-3 inline" /></span>;
  const down = pct <= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 font-semibold ${down ? "text-green-600" : "text-red-600"} ${className}`}>
      {down ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
      {down ? "−" : "+"}{Math.abs(pct).toLocaleString("id-ID", { maximumFractionDigits: 1 })}%
    </span>
  );
}

function OverlayTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-xl p-3 text-xs min-w-[170px]">
      <div className="font-bold text-slate-800 mb-2">Tanggal {label}</div>
      {payload.filter((p) => p.value != null).map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-5 py-0.5">
          <span className="flex items-center gap-1.5 text-slate-600">
            <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
            {p.name}
          </span>
          <span className="font-semibold tabular-nums text-slate-800">{fmtNum(p.value)} kWh</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Side-by-side comparison of two selected months.
 * props: items = [{date:'YYYY-MM-DD', values:{key:kwh}}] (all data, unfiltered),
 *        keys = column names, entityLabel = 'TSS' | 'Gedung', testIdPrefix
 */
export default function MonthComparison({ items, keys, entityLabel = "Unit", testIdPrefix = "cmp" }) {
  const months = useMemo(
    () => [...new Set(items.map((it) => it.date.slice(0, 7)))].sort(),
    [items]
  );

  const [monthA, setMonthA] = useState("");
  const [monthB, setMonthB] = useState("");

  useEffect(() => {
    if (months.length >= 2) {
      setMonthA((a) => (a && months.includes(a) ? a : months[months.length - 2]));
      setMonthB((b) => (b && months.includes(b) ? b : months[months.length - 1]));
    } else if (months.length === 1) {
      setMonthA(months[0]);
      setMonthB(months[0]);
    }
  }, [months]);

  const stats = useMemo(() => {
    const calc = (m) => {
      const rows = items.filter((it) => it.date.startsWith(m));
      const perKey = {};
      for (const k of keys) perKey[k] = rows.reduce((s, it) => s + (it.values?.[k] ?? 0), 0);
      const total = keys.reduce((s, k) => s + perKey[k], 0);
      const daily = {};
      for (const it of rows) {
        const day = Number(it.date.slice(8, 10));
        daily[day] = keys.reduce((s, k) => s + (it.values?.[k] ?? 0), 0);
      }
      return { perKey, total, daily, days: rows.length };
    };
    return { A: monthA ? calc(monthA) : null, B: monthB ? calc(monthB) : null };
  }, [items, keys, monthA, monthB]);

  const overlay = useMemo(() => {
    if (!stats.A || !stats.B) return [];
    const maxDay = Math.max(
      ...Object.keys(stats.A.daily).map(Number),
      ...Object.keys(stats.B.daily).map(Number),
      0
    );
    const out = [];
    for (let d = 1; d <= maxDay; d++) {
      out.push({ day: d, A: stats.A.daily[d] ?? null, B: stats.B.daily[d] ?? null });
    }
    return out;
  }, [stats]);

  if (months.length < 2) {
    return (
      <div className="py-8 text-center text-sm text-slate-400" data-testid={`${testIdPrefix}-cmp-empty`}>
        Perlu data minimal 2 bulan untuk membandingkan. Upload logsheet bulan lainnya terlebih dahulu.
      </div>
    );
  }

  const totalDelta =
    stats.A?.total > 0 && stats.B ? ((stats.B.total - stats.A.total) / stats.A.total) * 100 : null;
  const eA = fmtEnergy(stats.A?.total);
  const eB = fmtEnergy(stats.B?.total);

  return (
    <div className="space-y-5" data-testid={`${testIdPrefix}-cmp`}>
      {/* Month pickers */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Bulan A</div>
          <Select value={monthA} onValueChange={setMonthA}>
            <SelectTrigger className="h-9 w-40 text-xs font-semibold" data-testid={`${testIdPrefix}-cmp-month-a`}>
              <SelectValue placeholder="Pilih bulan" />
            </SelectTrigger>
            <SelectContent>
              {months.map((m) => (
                <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="pb-2 text-slate-300"><ArrowLeftRight className="w-4 h-4" /></div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Bulan B</div>
          <Select value={monthB} onValueChange={setMonthB}>
            <SelectTrigger className="h-9 w-40 text-xs font-semibold" data-testid={`${testIdPrefix}-cmp-month-b`}>
              <SelectValue placeholder="Pilih bulan" />
            </SelectTrigger>
            <SelectContent>
              {months.map((m) => (
                <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl border border-slate-200/70 p-4 bg-slate-50/60" data-testid={`${testIdPrefix}-cmp-total-a`}>
          <div className="flex items-center gap-2 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: COLOR_A }} />
            {monthLabel(monthA)}
          </div>
          <div className="text-2xl font-black text-slate-800 tabular-nums mt-1.5">
            {eA.v} <span className="text-sm font-bold text-slate-400">{eA.u}</span>
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">{stats.A?.days ?? 0} hari data</div>
        </div>
        <div className="rounded-xl border border-blue-200/70 p-4 bg-blue-50/50" data-testid={`${testIdPrefix}-cmp-total-b`}>
          <div className="flex items-center gap-2 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: COLOR_B }} />
            {monthLabel(monthB)}
          </div>
          <div className="text-2xl font-black text-slate-800 tabular-nums mt-1.5">
            {eB.v} <span className="text-sm font-bold text-slate-400">{eB.u}</span>
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">{stats.B?.days ?? 0} hari data</div>
        </div>
        <div className="rounded-xl border border-slate-200/70 p-4" data-testid={`${testIdPrefix}-cmp-delta`}>
          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Perubahan (B vs A)</div>
          <div className="text-2xl font-black tabular-nums mt-1.5">
            <DeltaPct pct={totalDelta} className="!text-2xl" />
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">
            Selisih {fmtNum(Math.abs((stats.B?.total ?? 0) - (stats.A?.total ?? 0)))} kWh
          </div>
        </div>
      </div>

      {/* Daily overlay chart */}
      <div>
        <div className="text-xs font-bold text-slate-600 mb-2">
          Tren harian per tanggal — total {entityLabel.toLowerCase() === "gedung" ? "seluruh gedung" : "4 TSS"}
        </div>
        <div className="h-[280px]" data-testid={`${testIdPrefix}-cmp-chart`}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={overlay} margin={{ top: 8, right: 16, bottom: 4, left: 14 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} label={{ value: "Tanggal", position: "insideBottomRight", offset: -2, style: { fontSize: 10, fill: "#94a3b8" } }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtNum(v)} width={72}
                label={{ value: "Konsumsi (kWh)", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#64748b", fontWeight: 600 } }} />
              <Tooltip content={<OverlayTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line name={monthLabel(monthA)} dataKey="A" stroke={COLOR_A} strokeWidth={2} dot={{ r: 2.5, strokeWidth: 0, fill: COLOR_A }} type="monotone" connectNulls />
              <Line name={monthLabel(monthB)} dataKey="B" stroke={COLOR_B} strokeWidth={2.5} dot={{ r: 2.5, strokeWidth: 0, fill: COLOR_B }} type="monotone" connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Per-entity delta table */}
      <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid={`${testIdPrefix}-cmp-table`}>
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px]">
              <th className="text-left px-3 py-2.5 font-bold">{entityLabel}</th>
              <th className="text-right px-3 py-2.5 font-bold whitespace-nowrap">{monthLabel(monthA)} (kWh)</th>
              <th className="text-right px-3 py-2.5 font-bold whitespace-nowrap">{monthLabel(monthB)} (kWh)</th>
              <th className="text-right px-3 py-2.5 font-bold whitespace-nowrap">Selisih (kWh)</th>
              <th className="text-right px-3 py-2.5 font-bold">Perubahan</th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => {
              const a = stats.A?.perKey[k] ?? 0;
              const b = stats.B?.perKey[k] ?? 0;
              const pct = a > 0 ? ((b - a) / a) * 100 : null;
              return (
                <tr key={k} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <td className="px-3 py-2 font-semibold text-slate-700 whitespace-nowrap">{k}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-600">{fmtNum(a)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-800 font-semibold">{fmtNum(b)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums font-medium ${b - a <= 0 ? "text-green-600" : "text-red-600"}`}>
                    {b - a >= 0 ? "+" : "−"}{fmtNum(Math.abs(b - a))}
                  </td>
                  <td className="px-3 py-2 text-right"><DeltaPct pct={pct} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
